import * as React from "react";
import {
	forwardRef,
	PropsWithChildren,
	useCallback,
	useMemo,
	useRef,
} from "react";
import { TileLayer } from "react-leaflet";
import L, { LatLngExpression, Map as LeafletMap } from "leaflet";

import "mapbox-gl-leaflet";
import StyleProps from "./internal/types/StyleProps";
import { ZoomController } from "./components/ZoomController";
import { CenterController } from "./components/CenterController";
import MapBase from "./components/MapBase";
import Root from "./components/Root";
import InternalPolygonEditor from "./components/PolygonEditor";
import { setRefValue } from "./internal/utils/controller";
import useChanged from "./internal/hooks/useChanged";
import useRefSize from "./internal/hooks/useRefSize";

const MAPMD_STYLE_URL =
	process.env.REACT_APP_MAPMD_STYLE_URL ??
	"https://map.md/api/tiles/styles/map/style.json";

const DEFAULT_OSM_TILE_URL =
	process.env.REACT_APP_OSM_TILE_URL ??
	"https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";

const OSM_ATTRIBUTION =
	'&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

const DEFAULT_LANGUAGE = "uk";

const getProvider = (
	nameMap: string | undefined,
): "mapmd" | "google" | "visicom" | "osm" => {
	switch (nameMap) {
		case "mapmd":
			return "mapmd";
		case "google":
			return "google";
		case "visicom":
			return "visicom";
		case "osm":
		case "openstreetmap":
			return "osm";
		default:
			return "visicom";
	}
};

const VisicomTile: React.FC<{
	url: string;
	language?: string;
	maxZoom?: number;
	minZoom?: number;
}> = ({ url, language, maxZoom, minZoom }) => {
	const layerRef = useRef<L.TileLayer | null>(null);
	const handleTileError = () => {
		if (language && language !== DEFAULT_LANGUAGE && layerRef.current) {
			console.warn(
				`Visicom failed with language "${language}". Use fallback "${DEFAULT_LANGUAGE}".`,
			);
			const fallbackUrl = url.includes("lang=")
				? url.replace(/lang=[^&]+/, `lang=${DEFAULT_LANGUAGE}`)
				: url;
			layerRef.current.setUrl(fallbackUrl);
		}
	};

	return (
		<TileLayer
			ref={layerRef as any}
			url={url}
			subdomains="123"
			maxZoom={maxZoom}
			minZoom={minZoom}
			tms
			eventHandlers={{ tileerror: handleTileError }}
		/>
	);
};

const MapComponent = forwardRef<LeafletMap, Map.Props>(
	(
		{
			children,
			style,
			className,
			language,
			tileURL,
			center,
			zoomControl = true,
			maxZoom,
			minZoom,
			zoom,
			hasRasterization = true,
			nameMap,
			mapToken,
			onChangeZoom,
			onChangeCenter,
			onInit,
			onReady,
		},
		ref,
	) => {
		const provider = getProvider(nameMap);

		const defaultTileURL = useMemo(
			() =>
				`https://tms2.visicom.ua/2.0.0/planet3v2/base/{z}/{x}/{y}.png?lang=${
					language || DEFAULT_LANGUAGE
				}`,
			[language],
		);

		const { ref: rootRef, size: rootSize } = useRefSize();
		const mapRef = useRef<LeafletMap | null>(null);

		useChanged(
			() => {
				if (mapRef.current) {
					mapRef.current.invalidateSize();
				}
			},
			rootSize,
			true,
		);

		const onInitRef = useRef(onInit);
		const onReadyRef = useRef(onReady);
		onInitRef.current = onInit;
		onReadyRef.current = onReady;

		const mapRefSetter = useCallback(
			async (map: LeafletMap | null) => {
				mapRef.current = map;
				setRefValue(ref, map);

				if (!map) return;

				onInitRef.current?.(map);

				try {
					if (provider === "mapmd") {
						if (!mapToken) {
							throw new Error("Mapbox access token is missing");
						}
						(
							L as unknown as {
								mapboxGL: (o: object) => {
									addTo: (m: LeafletMap) => void;
								};
							}
						)
							.mapboxGL({
								accessToken: mapToken,
								style: MAPMD_STYLE_URL,
							})
							.addTo(map);
					}

					if (provider === "google") {
						if (!mapToken) {
							throw new Error("Google Maps API key is missing");
						} else if ((map as any).attributionControl?.setPrefix) {
							(map as any).attributionControl.setPrefix("");
						}
					}
				} catch (err) {
					console.error("Failed to load map layer:", err);
				}

				map.invalidateSize();

				onReadyRef.current?.();
			},
			[ref, provider, maxZoom, mapToken],
		);

		return (
			<Root ref={rootRef}>
				<MapBase
					ref={mapRefSetter}
					style={style}
					className={className}
					center={center}
					doubleClickZoom={false}
					zoom={zoom}
					zoomControl={false}
					minZoom={minZoom}
					maxZoom={maxZoom}
				>
					<CenterController onChangeCenter={onChangeCenter} />
					<ZoomController
						showButtons={zoomControl}
						onChangeZoom={onChangeZoom}
					/>

					{hasRasterization && provider === "visicom" && (
						<VisicomTile
							url={tileURL ?? defaultTileURL}
							language={language}
							maxZoom={maxZoom}
							minZoom={minZoom}
						/>
					)}

					{provider === "google" && (
						<TileLayer
							url={`https://mt{s}.google.com/vt/lyrs=m&hl=${
								language || DEFAULT_LANGUAGE
							}&x={x}&y={y}&z={z}&key=${mapToken}`}
							subdomains="0123"
							maxZoom={maxZoom}
							minZoom={minZoom}
							attribution="Google"
						/>
					)}

					{provider === "osm" && (
						<TileLayer
							url={tileURL ?? DEFAULT_OSM_TILE_URL}
							subdomains="abc"
							maxZoom={maxZoom}
							minZoom={minZoom}
							attribution={OSM_ATTRIBUTION}
						/>
					)}

					{children}
				</MapBase>
			</Root>
		);
	},
);

const Map = Object.assign(MapComponent, {
	PolygonEditor: InternalPolygonEditor,
});

declare namespace Map {
	interface Props extends PropsWithChildren, StyleProps {
		language?: string;
		nameMap?: string;
		tileURL?: string;
		hasRasterization?: boolean;
		center: LatLngExpression;
		zoomControl?: boolean;
		maxZoom?: number;
		minZoom?: number;
		zoom?: number;
		mapToken?: string;
		onChangeCenter?: (coordinates: LatLngExpression) => void;
		onChangeZoom?: (zoom: number) => void;
		onInit?: (map: LeafletMap) => void;
		onReady?: () => void;
	}

	namespace PolygonEditor {
		type Value = InternalPolygonEditor.Value;
		type Controller = InternalPolygonEditor.Controller;

		namespace Controller {
			type Context = InternalPolygonEditor.Controller.Context;
		}
	}
}

export default Map;
